import React, { useState, useEffect } from "react";

function AdminReleaseMaterial() {
  const [backendUrl, setBackendUrl] = useState("");
  const [mode, setMode] = useState("individual"); // individual | group

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const [releaseDate, setReleaseDate] = useState("");
  const [status, setStatus] = useState("");

  /* ================= LOAD BACKEND URL ================= */
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";
    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      }
    };
    document.body.appendChild(script);
  }, []);

  /* ================= FETCH STUDENTS ================= */
  useEffect(() => {
    if (!backendUrl) return;

    fetch(`${backendUrl}/students`)
      .then(res => res.json())
      .then(data => setStudents(Array.isArray(data) ? data : []))
      .catch(() => setStudents([]));
  }, [backendUrl]);

  /* ================= FETCH COURSES ================= */
  useEffect(() => {
    if (!backendUrl) return;

    let url = null;
    if (mode === "group") {
      url = `${backendUrl}/all-courses`;
    } else if (selectedStudent) {
      url = `${backendUrl}/student-enrolled-courses/${selectedStudent}`;
    }

    if (!url) return;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        const normalized = Array.isArray(data)
          ? data.map(c => ({
              ID: Number(c.Course_ID ?? c.ID),
              Course_Name: c.Course_Name
            }))
          : [];
        setCourses(normalized);
      })
      .catch(() => setCourses([]));
  }, [mode, selectedStudent, backendUrl]);

  /* ================= FETCH MATERIALS + SESSIONS ================= */
  useEffect(() => {
    if (!backendUrl || !selectedCourse) return;

    fetch(`${backendUrl}/course-uploads/${selectedCourse}`)
      .then(res => res.json())
      .then(data => setMaterials(Array.isArray(data) ? data : []))
      .catch(() => setMaterials([]));

    if (mode === "individual") {
      fetch(`${backendUrl}/course-sessions/${selectedCourse}`)
        .then(res => res.json())
        .then(data => setSessions(Array.isArray(data) ? data : []))
        .catch(() => setSessions([]));
    } else {
      setSessions([]);
    }
  }, [selectedCourse, mode, backendUrl]);

  /* ================= RESET CASCADING ================= */
  useEffect(() => {
    setSelectedCourse(null);
    setSelectedSession(null);
    setSelectedMaterial(null);
    setCourses([]);
    setSessions([]);
    setMaterials([]);
  }, [selectedStudent]);

  useEffect(() => {
    setSelectedSession(null);
    setSelectedMaterial(null);
    setSessions([]);
    setMaterials([]);
  }, [selectedCourse]);

  /* ================= RELEASE MATERIAL ================= */
  const handleRelease = async () => {
    console.log("🚀 Release clicked");

    setStatus("");

    if (!selectedCourse || !selectedMaterial || !releaseDate) {
      setStatus("⚠️ Please fill all required fields.");
      return;
    }

    if (mode === "individual" && !selectedStudent) {
      setStatus("⚠️ Please select a student.");
      return;
    }

    if (mode === "individual" && !selectedSession) {
      setStatus("⚠️ Please select a session.");
      return;
    }

    const formattedDate = new Date(releaseDate)
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    const payload = {
      studentId: mode === "group" ? null : Number(selectedStudent),
      courseId: Number(selectedCourse),
      uploadId: Number(selectedMaterial),
      sessionId: mode === "group" ? null : Number(selectedSession),
      releaseDate: formattedDate,
      releaseType: mode
    };

    console.log("📦 Release Payload:", payload);

    try {
      const res = await fetch(`${backendUrl}/release-material`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        console.error("❌ Backend error:", data);
        setStatus(`❌ ${data.message || "Backend rejected the request"}`);
        return;
      }

      setStatus("✅ Material released successfully.");

      setSelectedStudent(null);
      setSelectedCourse(null);
      setSelectedSession(null);
      setSelectedMaterial(null);
      setReleaseDate("");
    } catch (err) {
      console.error("❌ Network / Server error", err);
      setStatus("❌ Server error while releasing material.");
    }
  };

  /* ================= UI ================= */
  return (
    <div style={container}>
      <h2 style={header}>📤 Release Learning Materials</h2>

      <div style={tabContainer}>
        <button
          style={mode === "individual" ? activeTab : inactiveTab}
          onClick={() => {
            setMode("individual");
            setSelectedStudent(null);
          }}
        >
          👤 Individual Release
        </button>
        <button
          style={mode === "group" ? activeTab : inactiveTab}
          onClick={() => {
            setMode("group");
            setSelectedStudent(null);
          }}
        >
          👥 Group Release
        </button>
      </div>

      {mode === "individual" && (
        <div style={card}>
          <label style={label}>Select Student:</label>
          <select
            value={selectedStudent ?? ""}
            onChange={e => setSelectedStudent(Number(e.target.value))}
            style={select}
          >
            <option value="">-- Select Student --</option>
            {students.map(s => (
              <option key={s.User_ID} value={s.User_ID}>
                {s.Username}
              </option>
            ))}
          </select>

          <label style={label}>Select Course:</label>
          <select
            value={selectedCourse ?? ""}
            onChange={e => setSelectedCourse(Number(e.target.value))}
            style={select}
            disabled={!selectedStudent}
          >
            <option value="">-- Select Course --</option>
            {courses.map(c => (
              <option key={c.ID} value={c.ID}>
                {c.Course_Name}
              </option>
            ))}
          </select>

          <label style={label}>Select Session:</label>
          <select
            value={selectedSession ?? ""}
            onChange={e => setSelectedSession(Number(e.target.value))}
            style={select}
            disabled={!selectedCourse}
          >
            <option value="">-- Select Session --</option>
            {sessions.map(s => (
              <option key={s.Session_ID} value={s.Session_ID}>
                {s.SessionName}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === "group" && (
        <div style={card}>
          <label style={label}>Select Course:</label>
          <select
            value={selectedCourse ?? ""}
            onChange={e => setSelectedCourse(Number(e.target.value))}
            style={select}
          >
            <option value="">-- Select Course --</option>
            {courses.map(c => (
              <option key={c.ID} value={c.ID}>
                {c.Course_Name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={card}>
        <label style={label}>Select Material:</label>
        <select
          value={selectedMaterial ?? ""}
          onChange={e => setSelectedMaterial(Number(e.target.value))}
          style={select}
        >
          <option value="">-- Select Material --</option>
          {materials.map(m => (
            <option key={m.Upload_ID} value={m.Upload_ID}>
              {m.Material_Name}
            </option>
          ))}
        </select>

        <label style={label}>Release Date:</label>
        <input
          type="datetime-local"
          value={releaseDate}
          onChange={e => setReleaseDate(e.target.value)}
          style={select}
        />
      </div>

      <button onClick={handleRelease} style={submitBtn}>
        Release Material
      </button>

      {status && <p style={statusText(status)}>{status}</p>}
    </div>
  );
}

/* ================= STYLES ================= */

const container = {
  maxWidth: 900,
  margin: "20px auto",
  padding: 30,
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  fontFamily: "Roboto, Arial, sans-serif"
};

const header = {
  color: "#E30016",
  textAlign: "center",
  marginBottom: 20
};

const card = {
  border: "1px solid #ddd",
  borderRadius: 8,
  padding: 20,
  marginBottom: 20,
  background: "#fafafa"
};

const label = {
  fontWeight: 500,
  marginBottom: 5,
  display: "block"
};

const select = {
  width: "100%",
  padding: 10,
  borderRadius: 6,
  border: "1px solid #ccc",
  marginBottom: 12
};

const submitBtn = {
  width: "40%",
  padding: 15,
  background: "#E30016",
  color: "#fff",
  fontWeight: 650,
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  display: "block",
  margin: "0 auto",
  fontSize: "18px"
};

const statusText = text => ({
  color: text.startsWith("✅") ? "green" : "red",
  textAlign: "center",
  marginTop: 15
});

const tabContainer = {
  display: "flex",
  justifyContent: "center",
  marginBottom: 20,
  gap: 10
};

const activeTab = {
  padding: "10px 20px",
  borderRadius: 6,
  border: "2px solid #E30016",
  background: "#E30016",
  color: "#fff",
  cursor: "pointer"
};

const inactiveTab = {
  padding: "10px 20px",
  borderRadius: 6,
  border: "2px solid #ccc",
  background: "#fff",
  color: "#444",
  cursor: "pointer"
};

export default AdminReleaseMaterial;
